<template>
  <!-- ===================================================================
       APPLICATION MOBILE — arbre distinct de celui du site.
       Le site est servi inchangé par le bloc suivant.
       =================================================================== -->
  <div v-if="isNative && (hasAvailableQuotes || loading || fetchAttempted)" class="rvi">
    <p class="rvi__label">Assurance annulation<span class="rvi__brand">Chapka</span></p>

    <div v-if="loading && !hasAvailableQuotes" class="rvi__wait">
      <span class="rvi__skel" v-for="n in 2" :key="'rvi-skel-' + n"></span>
    </div>

    <p v-if="loading && hasAvailableQuotes" class="rvi__reload">Recalcul du prix…</p>

    <div v-if="!loading && !hasAvailableQuotes && fetchAttempted" class="rvi__ko">
      <p>{{ errorReason || 'Les assurances Chapka ne sont pas disponibles pour cette réservation.' }}</p>
      <p v-if="!errorReason" class="rvi__ko-sub">
        L'assurance doit être souscrite au moins 10 jours avant le départ.
      </p>
    </div>

    <ul v-if="hasAvailableQuotes" class="rvi__list">
      <li v-for="formule in availableFormulesWithQuotes" :key="formule.code">
        <label class="rvi__card" :class="{ 'is-on': selectedFormule === formule.code && accepted }">
          <input
            type="checkbox"
            class="rvi__input"
            :checked="selectedFormule === formule.code && accepted"
            @change="onSelectFormule(formule.code)"
          >
          <span v-if="formule.recommended" class="rvi__reco">Recommandé</span>

          <span class="rvi__head">
            <span class="rvi__mark" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="3.6" stroke-linecap="round" stroke-linejoin="round"><path d="m5 13 4 4L19 7"/></svg>
            </span>
            <span class="rvi__name">{{ formule.nom }}</span>
            <span v-if="quotes[formule.code]" class="rvi__price">+ {{ rviPrix(quotes[formule.code]) }}</span>
            <span v-else class="rvi__price-wait" aria-label="Calcul du tarif en cours"></span>
          </span>

          <span class="rvi__desc">{{ formule.description }}</span>

          <!-- Documents imposés par l'assureur : ils ne peuvent être ni
               retirés, ni repliés. `@click.stop` évite que l'ouverture d'un
               document ne coche la formule au passage. -->
          <span v-if="quotes[formule.code]" class="rvi__links">
            <a :href="quotes[formule.code].cg_url" target="_blank" rel="noopener noreferrer" @click.stop>Conditions générales</a>
            <a :href="quotes[formule.code].ipid_url" target="_blank" rel="noopener noreferrer" @click.stop>Fiche produit (IPID)</a>
            <a v-if="quotes[formule.code].cs_url" :href="quotes[formule.code].cs_url" target="_blank" rel="noopener noreferrer" @click.stop>Conditions spéciales</a>
          </span>
        </label>
      </li>
    </ul>

    <div v-if="accepted && selectedFormule && hasAvailableQuotes" class="rvi__people">
      <p class="rvi__people-title">Informations des assurés</p>
      <p class="rvi__people-sub">Exigées par l'assureur pour établir le contrat.</p>

      <div v-for="(participant, index) in participants" :key="index" class="rvi__person">
        <p class="rvi__person-num">Participant {{ index + 1 }}</p>
        <div class="rvi__fields">
          <select
            v-model="participant.genre"
            @change="validateAndEmit"
            class="rvi__sel"
            :class="{ 'is-ko': errors[index] && errors[index].genre }"
            :aria-label="'Civilité du participant ' + (index + 1)"
          >
            <option value="">Civ.</option>
            <option value="M.">M.</option>
            <option value="Mme">Mme</option>
          </select>
          <input
            type="text"
            v-model="participant.prenom"
            @input="validateAndEmit"
            placeholder="Prénom"
            autocomplete="given-name"
            class="rvi__in"
            :class="{ 'is-ko': errors[index] && errors[index].prenom }"
            :aria-label="'Prénom du participant ' + (index + 1)"
          >
          <input
            type="text"
            v-model="participant.nom"
            @input="validateAndEmit"
            placeholder="Nom"
            autocomplete="family-name"
            class="rvi__in"
            :class="{ 'is-ko': errors[index] && errors[index].nom }"
            :aria-label="'Nom du participant ' + (index + 1)"
          >
        </div>
      </div>
    </div>
  </div>

  <!-- ===================================================================
       SITE WEB — balisage d'origine, inchangé.
       =================================================================== -->
  <div class="chapka-insurance-wrapper" v-else-if="hasAvailableQuotes || loading || fetchAttempted">
    <!-- Indicateur de rechargement -->
    <div v-if="loading && hasAvailableQuotes" class="reloading-indicator">
      <div class="small-spinner"></div>
      <span>Recalcul du prix...</span>
    </div>

    <!-- Message initial de chargement (quand on n'a pas encore de devis) -->
    <div v-if="loading && !hasAvailableQuotes" class="insurance-loading-inline">
      <div class="loading-spinner"></div>
      <span>Calcul des assurances...</span>
    </div>

    <!-- Squelettes de chargement (application mobile uniquement) -->
    <div v-if="isNative && loading && !hasAvailableQuotes" class="insurance-skeletons">
      <div class="insurance-skeleton oc-skeleton" v-for="n in 2" :key="'insurance-skeleton-' + n"></div>
    </div>

    <!-- Message d'erreur si aucun devis disponible après tentative -->
    <div v-if="!loading && !hasAvailableQuotes && fetchAttempted" class="insurance-error">
      <p>❌ {{ errorReason || 'Les assurances Chapka ne sont pas disponibles pour cette réservation.' }}</p>
      <p class="error-detail" v-if="!errorReason">L'assurance nécessite une réservation au moins 10 jours avant le départ. Vérifiez vos dates ou contactez le support.</p>
    </div>

    <!-- Sélection de la formule -->
    <div class="insurance-formules" v-if="hasAvailableQuotes">
      <div
        v-for="formule in availableFormulesWithQuotes"
        :key="formule.code"
        class="insurance-option"
        :class="{ 'insurance-selected': selectedFormule === formule.code && accepted }"
      >
        <label class="insurance-label">
          <div class="checkbox-wrapper">
            <input
              type="checkbox"
              :checked="selectedFormule === formule.code && accepted"
              @change="onSelectFormule(formule.code)"
              class="insurance-checkbox"
            >
          </div>

          <div class="insurance-card-content">
            <div class="insurance-header">
              <div class="insurance-title-block">
                <h4 class="insurance-title">ASSURANCE {{ formule.nom }}</h4>
                <span v-if="formule.recommended" class="insurance-badge">Recommandé</span>
              </div>
              <div class="insurance-price" v-if="quotes[formule.code]">
                + {{ quotes[formule.code].premium }} {{ quotes[formule.code].currency }}
              </div>
              <div class="insurance-price-loading" v-else>
                <div class="small-spinner"></div>
              </div>
            </div>

            <p class="insurance-description">
              {{ formule.description }}
            </p>

            <div class="insurance-links" v-if="quotes[formule.code]">
              <a :href="quotes[formule.code].cg_url" target="_blank" rel="noopener noreferrer">
                Conditions Générales
              </a>
              <a :href="quotes[formule.code].ipid_url" target="_blank" rel="noopener noreferrer">
                Fiche produit (IPID)
              </a>
              <a v-if="quotes[formule.code].cs_url" :href="quotes[formule.code].cs_url" target="_blank" rel="noopener noreferrer">
                Conditions Spéciales
              </a>
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- Formulaire des participants (seulement si une formule est acceptée) -->
    <div v-if="accepted && selectedFormule && hasAvailableQuotes" class="participants-form">
      <h4 class="participants-title">
        Informations des assurés
      </h4>
      <p class="participants-subtitle">
        Veuillez renseigner les informations de chaque participant.
      </p>

      <div
        v-for="(participant, index) in participants"
        :key="index"
        class="participant-row"
      >
        <div class="participant-header">
          <span class="participant-number">Participant {{ index + 1 }}</span>
        </div>

        <div class="participant-fields">
          <div class="field-group field-genre oc-field">
            <label :for="'genre-' + index" class="oc-field__label">Civilité</label>
            <select
              :id="'genre-' + index"
              v-model="participant.genre"
              @change="validateAndEmit"
              class="oc-field__control"
              :class="{ 'field-error': errors[index]?.genre }"
            >
              <option value="">Choix...</option>
              <option value="M.">M.</option>
              <option value="Mme">Mme</option>
            </select>
            <span v-if="errors[index]?.genre" class="error-message oc-field__error">
              {{ errors[index].genre }}
            </span>
          </div>

          <div class="field-group field-prenom oc-field">
            <label :for="'prenom-' + index" class="oc-field__label">Prénom</label>
            <input
              type="text"
              :id="'prenom-' + index"
              v-model="participant.prenom"
              @input="validateAndEmit"
              placeholder="Prénom"
              class="oc-field__control"
              :class="{ 'field-error': errors[index]?.prenom }"
            >
            <span v-if="errors[index]?.prenom" class="error-message oc-field__error">
              {{ errors[index].prenom }}
            </span>
          </div>

          <div class="field-group field-nom oc-field">
            <label :for="'nom-' + index" class="oc-field__label">Nom</label>
            <input
              type="text"
              :id="'nom-' + index"
              v-model="participant.nom"
              @input="validateAndEmit"
              placeholder="Nom"
              class="oc-field__control"
              :class="{ 'field-error': errors[index]?.nom }"
            >
            <span v-if="errors[index]?.nom" class="error-message oc-field__error">
              {{ errors[index].nom }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, watch, onMounted, reactive, computed } from 'vue';
import { getInsuranceQuote, isInsuranceEligible } from '@/api/chapka';
import { isNative } from '@/api/base';

export default {
  name: 'ChapkaInsurance',

  props: {
    offerType: { type: String, required: true },
    offerId: { type: String, default: '' },
    participantCount: { type: Number, required: true },
    startDate: { type: String, required: true },
    endDate: { type: String, required: true },
    totalAmount: { type: Number, required: true },
    destination: { type: String, default: 'FR' }
  },

  emits: ['update:insurance', 'update:participants', 'quote-loaded'],

  setup(props, { emit }) {
    // Calculer la durée du séjour pour déterminer le produit
    const getTripDuration = () => {
      const start = new Date(props.startDate.split('/').reverse().join('-'));
      const end = new Date(props.endDate.split('/').reverse().join('-'));
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays;
    };

    // Déterminer le produit en fonction de la durée (CAP-EXPLORER pour > 3 jours, CAP-ASSISTANCE pour ≤ 3 jours)
    const getRecommendedProduct = () => {
      const duration = getTripDuration();
      return duration > 3 ? 'CAP-EXPLORER' : 'CAP-ASSISTANCE';
    };

    // Définition des formules disponibles
    const availableFormules = ref([
      {
        code: 'AN',
        nom: 'Annulation',
        description: 'Protection annulation et interruption de séjour. Couvrez-vous contre les imprévus !',
        recommended: true,
        product: 'CAP-EXPLORER',
        requiresAdvanceBooking: true // Nécessite réservation 10 jours avant
      },
      {
        code: 'AS',
        nom: 'Assistance',
        description: 'Assistance rapatriement pour toutes vos activités. Pas de restriction de délai !',
        recommended: false,
        product: 'CAP-ASSISTANCE',
        requiresAdvanceBooking: false // Pas de restriction
      },
      {
        code: 'MR',
        nom: 'Multi-risque',
        description: 'Protection complète : annulation, bagages, assistance rapatriement et responsabilité civile.',
        recommended: false,
        product: 'CAP-EXPLORER',
        requiresAdvanceBooking: true // Nécessite réservation 10 jours avant
      }
    ]);

    const quotes = reactive({});
    const loading = ref(false);
    const accepted = ref(false);
    const selectedFormule = ref(null);
    const participants = ref([]);
    const errors = ref([]);
    const hasAvailableQuotes = ref(false);
    const fetchAttempted = ref(false);
    const errorReason = ref(''); // Pour stocker la raison de l'erreur

    // Garder trace des derniers paramètres pour éviter les appels inutiles
    const lastFetchParams = ref(null);

    // Filtrer les formules pour ne garder que celles qui ont un devis valide
    const availableFormulesWithQuotes = computed(() => {
      return availableFormules.value.filter(formule => quotes[formule.code] !== null && quotes[formule.code] !== undefined);
    });

    const initializeParticipants = (count) => {
      const newParticipants = [];
      for (let i = 0; i < count; i++) {
        if (participants.value[i]) {
          newParticipants.push(participants.value[i]);
        } else {
          newParticipants.push({ genre: '', prenom: '', nom: '' });
        }
      }
      participants.value = newParticipants;
      errors.value = newParticipants.map(() => ({}));
    };

    const fetchQuotes = async () => {
      if (!isInsuranceEligible(props.offerType)) {
        return;
      }
      if (!props.startDate || !props.endDate || !props.participantCount || !props.totalAmount) {
        return;
      }

      // VALIDATION: Vérifier que le départ est au moins à 10 jours (uniquement pour AN et MR)
      const departDate = new Date(props.startDate.split('/').reverse().join('-'));
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const daysDiff = Math.ceil((departDate - today) / (1000 * 60 * 60 * 24));

      // Filtrer les formules qui nécessitent 10 jours d'avance si la condition n'est pas remplie
      const eligibleFormules = daysDiff < 10
        ? availableFormules.value.filter(f => !f.requiresAdvanceBooking)
        : availableFormules.value;

      if (eligibleFormules.length === 0) {
        errorReason.value = `Les assurances Annulation et Multi-risque nécessitent une réservation au moins 10 jours avant le départ. Votre départ est dans ${daysDiff} jour${daysDiff > 1 ? 's' : ''}.`;
        hasAvailableQuotes.value = false;
        fetchAttempted.value = true;
        loading.value = false;
        return;
      }

      // Vérifier si les paramètres ont changé
      const currentParams = JSON.stringify({
        offerType: props.offerType,
        participantCount: props.participantCount,
        startDate: props.startDate,
        endDate: props.endDate,
        totalAmount: props.totalAmount
      });

      if (lastFetchParams.value === currentParams) {
        return;
      }

      // Éviter les appels multiples pendant le chargement
      if (loading.value) {
        return;
      }

      lastFetchParams.value = currentParams;
      loading.value = true;
      errorReason.value = ''; // Réinitialiser le message d'erreur

      try {
        // Charger les devis pour les formules éligibles en parallèle
        const quotePromises = eligibleFormules.map(async (formule) => {
          try {
            const requestData = {
              offerId: props.offerId,
              type: props.offerType,
              formule: formule.code,
              produit: formule.product || getRecommendedProduct(), // Utiliser le produit spécifique de la formule ou le recommandé
              provenance: 'FR',
              destination: props.destination,
              nombre: props.participantCount,
              depart: props.startDate,
              retour: props.endDate,
              date_resa: new Date().toISOString().split('T')[0],
              montant_total: props.totalAmount
            };

            const result = await getInsuranceQuote(requestData);

            if (result) {
              quotes[formule.code] = result;
            } else {
              quotes[formule.code] = null;
            }
          } catch (error) {
            quotes[formule.code] = null;
          }
        });

        await Promise.all(quotePromises);

        const availableQuotes = Object.entries(quotes).filter(([, quote]) => quote !== null);

        hasAvailableQuotes.value = availableQuotes.length > 0;
        fetchAttempted.value = true;

        if (quotes.AN || quotes.MR) {
          emit('quote-loaded', quotes);
        }
      } catch (error) {
        fetchAttempted.value = true;
      } finally {
        loading.value = false;
      }
    };

    const validateParticipants = () => {
      let isValid = true;
      const newErrors = [];
      participants.value.forEach((p) => {
        const participantErrors = {};
        if (!p.genre) { participantErrors.genre = 'Requis'; isValid = false; }
        if (!p.prenom || p.prenom.trim().length < 2) { participantErrors.prenom = 'Requis'; isValid = false; }
        if (!p.nom || p.nom.trim().length < 2) { participantErrors.nom = 'Requis'; isValid = false; }
        newErrors.push(participantErrors);
      });
      errors.value = newErrors;
      return isValid;
    };

    const validateAndEmit = () => {
      const isValid = validateParticipants();
      emit('update:participants', { participants: participants.value, isValid: isValid });
    };

    const onSelectFormule = (formuleCode) => {
      if (selectedFormule.value === formuleCode && accepted.value) {
        // Décocher
        accepted.value = false;
        selectedFormule.value = null;
        emit('update:insurance', {
          accepted: false,
          premium: 0,
          quote: null,
          formule: null,
          produit: null
        });
      } else {
        // Cocher cette formule
        accepted.value = true;
        selectedFormule.value = formuleCode;
        const selectedQuote = quotes[formuleCode];
        const selectedFormuleData = availableFormules.value.find(f => f.code === formuleCode);

        emit('update:insurance', {
          accepted: true,
          premium: selectedQuote ? parseFloat(selectedQuote.premium) : 0,
          quote: selectedQuote,
          formule: formuleCode,
          produit: selectedFormuleData?.product || getRecommendedProduct()
        });

        if (accepted.value) validateAndEmit();
      }
    };

    watch(() => props.participantCount, (newCount) => {
      if (newCount > 0) initializeParticipants(newCount);
    }, { immediate: true });

    watch(() => [props.offerType, props.participantCount, props.startDate, props.endDate, props.totalAmount], () => {
      if (props.participantCount > 0 && props.startDate && props.endDate && props.totalAmount > 0) {
        fetchQuotes();
      }
    });

    onMounted(() => {
      if (props.participantCount > 0 && props.startDate && props.endDate && props.totalAmount > 0) {
        fetchQuotes();
      }
    });

    return {
      availableFormules,
      availableFormulesWithQuotes,
      quotes,
      loading,
      accepted,
      selectedFormule,
      participants,
      errors,
      hasAvailableQuotes,
      fetchAttempted,
      errorReason,
      onSelectFormule,
      validateAndEmit,
      /**
       * Tarif d'une formule. L'API renvoie « 54.00 » et « EUR » ; l'écran
       * affiche partout ailleurs des montants à la française.
       */
      rviPrix: (q) => {
        const n = Number(q && q.premium);
        const montant = Number.isFinite(n) ? n.toFixed(2).replace('.', ',') : (q && q.premium);
        const devise = q && q.currency === 'EUR' ? '€' : (q && q.currency) || '';
        return `${montant} ${devise}`.trim();
      },
      isNative
    };
  }
};
</script>
<style scoped>
/* Reset */
* {
  box-sizing: border-box;
}

.chapka-insurance-wrapper {
  margin: 30px 0;
  font-family: 'Dosis', sans-serif;
  width: 100%;
}

.insurance-formules {
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-bottom: 0;
}

/* --- La Carte Principale (Conteneur) --- */
.insurance-option {
  /* On retire le fond blanc dur pour un fond transparent ou semi-transparent */
  background: rgba(255, 255, 255, 0.5); 
  /* Bordure rouge fine par défaut pour rappeler le style des inputs */
  border: 2px solid #e74c3c; 
  border-radius: 20px; /* Tes arrondis standard */
  transition: all 0.3s ease;
  overflow: hidden;
  cursor: pointer;
  position: relative;
}

/* Effet au survol */
.insurance-option:hover {
  background: rgba(255, 255, 255, 0.8);
  box-shadow: 0 4px 15px rgba(231, 76, 60, 0.15);
}

/* État sélectionné : Fond plus présent et ombre */
.insurance-option.insurance-selected {
  background: #fff;
  border-width: 3px; /* Bordure un peu plus épaisse */
  box-shadow: 0 4px 20px rgba(231, 76, 60, 0.2);
}

.insurance-label {
  display: flex;
  padding: 20px 25px; /* Plus d'espace */
  gap: 20px;
  align-items: center; /* Centrage vertical important */
  cursor: pointer;
}

/* --- Checkbox stylisée --- */
.checkbox-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
}

.insurance-checkbox {
  width: 28px; /* Plus gros */
  height: 28px;
  cursor: pointer;
  accent-color: #e74c3c; /* Couleur rouge native moderne */
  margin: 0;
  transform: scale(1.1);
  transition: transform 0.2s;
}

.insurance-option:hover .insurance-checkbox {
  transform: scale(1.2);
}

.insurance-card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

/* --- Header : Titre + Prix sur la même ligne --- */
.insurance-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  width: 100%;
}

.insurance-title-block {
  display: flex;
  align-items: center;
  gap: 15px;
  flex-wrap: wrap;
}

.insurance-title {
  font-family: 'Gagalin', sans-serif;
  font-size: clamp(1.2rem, 2.5vw, 1.6rem); /* Un peu plus gros */
  color: #dd4024; /* Ton rouge foncé */
  margin: 0;
  line-height: 1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.insurance-badge {
  background: #e74c3c;
  color: white;
  font-size: 0.75rem;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: bold;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-family: 'Dosis', sans-serif;
}

.insurance-price {
  font-family: 'Gagalin', sans-serif;
  font-size: clamp(1.4rem, 2.5vw, 1.8rem);
  color: #dd4024;
  white-space: nowrap;
}

.insurance-price-loading {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #999;
}

.small-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* --- Description et Liens --- */
.insurance-description {
  margin: 8px 0 12px 0;
  color: #555;
  font-size: 1.1rem;
  line-height: 1.4;
  font-weight: 500;
}

.insurance-links {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 5px;
}

.insurance-links a {
  color: #333;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  border-bottom: 1px solid transparent;
  transition: all 0.2s;
  opacity: 0.7;
}

.insurance-links a:hover {
  color: #e74c3c;
  border-bottom-color: #e74c3c;
  opacity: 1;
}

/* --- Formulaire des participants --- */
.participants-form {
  margin-top: 15px;
  padding: 30px 25px 25px;
  background: rgba(255, 255, 255, 0.4);
  border: 2px solid #e74c3c;
  border-radius: 20px;
  animation: slideDown 0.4s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes slideDown {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.participants-title {
  font-family: 'Gagalin', sans-serif;
  font-size: 1.3rem;
  color: #333;
  margin: 0 0 15px 0;
}

.participants-subtitle {
  display: none;
}

.participant-row {
  margin-bottom: 15px;
  padding-bottom: 15px;
  border-bottom: 1px dashed rgba(231, 76, 60, 0.3);
}

.participant-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
  margin-bottom: 0;
}

.participant-header {
  margin-bottom: 10px;
}

.participant-number {
  font-weight: bold;
  color: #e74c3c;
  font-family: 'Gagalin', sans-serif;
  letter-spacing: 1px;
}

.participant-fields {
  display: grid;
  grid-template-columns: 100px 1fr 1fr;
  gap: 15px;
}

/* --- Inputs du formulaire (Style arrondi rouge) --- */
.field-group {
  display: flex;
  flex-direction: column;
}

.field-group label {
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 4px;
  margin-left: 10px; /* Alignement avec l'arrondi */
  font-weight: bold;
  text-transform: uppercase;
}

.field-group input,
.field-group select {
  background-color: white;
  color: #333;
  border: 2px solid #e74c3c;
  border-radius: 20px;
  padding: 10px 15px;
  font-size: 1rem;
  width: 100%;
  outline: none;
  font-family: 'Dosis', sans-serif;
  font-weight: 600;
}

.field-group input:focus,
.field-group select:focus {
  background-color: #fff;
  box-shadow: 0 0 0 3px rgba(231, 76, 60, 0.2);
}

.field-error {
  border-color: #ff0000 !important;
}

.error-message {
  color: #e74c3c;
  font-size: 0.8rem;
  margin-top: 5px;
  margin-left: 10px;
  font-weight: bold;
}

/* Loader et messages */
.insurance-loading {
  padding: 20px;
  text-align: center;
  color: #e74c3c;
  font-family: 'Gagalin', sans-serif;
}

.insurance-error {
  padding: 20px;
  text-align: center;
  background: rgba(231, 76, 60, 0.1);
  border: 2px solid #e74c3c;
  border-radius: 20px;
}

.insurance-error p {
  margin: 0 0 10px 0;
  color: #e74c3c;
  font-family: 'Dosis', sans-serif;
  font-weight: 600;
  font-size: 1rem;
}

.insurance-error p:last-child {
  margin-bottom: 0;
}

.insurance-error .error-detail {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.insurance-loading-inline {
  padding: 20px;
  text-align: center;
  color: #e74c3c;
  font-family: 'Gagalin', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.reloading-indicator {
  padding: 10px 20px;
  text-align: center;
  color: #e74c3c;
  font-family: 'Dosis', sans-serif;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: rgba(231, 76, 60, 0.1);
  border-radius: 10px;
  margin-bottom: 15px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #e74c3c;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

/* Responsive */
@media (max-width: 768px) {
  .participant-fields {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  .insurance-header {
    flex-direction: row;
  }

  .insurance-label {
    padding: 15px;
    gap: 15px;
  }

  .insurance-title {
    font-size: 1.1rem;
  }

  .insurance-price {
    font-size: 1.2rem;
  }
}

/* =========================================================================
   APPLICATION MOBILE (iOS / Android) — toutes les règles sont préfixées
   par body.is-native : elles sont inertes sur www.ouicsport.fr.

   Intention : les formules deviennent des cartes blanches sélectionnables
   posées sur le fond chaud. Plus de bordure rouge de 2px sur chaque bloc :
   le rouge de marque est réservé au prix et à l'état sélectionné.
   ========================================================================= */

body.is-native .chapka-insurance-wrapper {
  margin: var(--oc-sp-6) 0 0;
  font-family: var(--oc-font-text);
}

body.is-native .insurance-formules {
  gap: var(--oc-sp-3);
}

/* --- Carte de formule --- */
body.is-native .insurance-option {
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
  transition: border-color var(--oc-fast) var(--oc-ease),
              background var(--oc-fast) var(--oc-ease),
              box-shadow var(--oc-fast) var(--oc-ease);
}

/* Le survol n'existe pas au doigt : il resterait « collé » après un appui. */
body.is-native .insurance-option:hover {
  background: var(--oc-surface);
  box-shadow: var(--oc-e1);
}

body.is-native .insurance-option:active {
  background: var(--oc-surface-2);
}

body.is-native .insurance-option.insurance-selected,
body.is-native .insurance-option.insurance-selected:hover {
  background: var(--oc-brand-50);
  border-width: 1px;
  border-color: var(--oc-brand);
  box-shadow: var(--oc-e2);
}

body.is-native .insurance-label {
  padding: var(--oc-sp-4);
  gap: var(--oc-sp-2);
  align-items: flex-start;
}

/* --- Case à cocher : 28px visuels, 44px de zone tactile --- */
body.is-native .checkbox-wrapper {
  flex: 0 0 var(--oc-tap);
  width: var(--oc-tap);
  height: var(--oc-tap);
  margin: -8px 0 -8px -8px;
}

body.is-native .insurance-checkbox,
body.is-native .insurance-option:hover .insurance-checkbox {
  width: 28px;
  height: 28px;
  transform: none;
  accent-color: var(--oc-brand);
}

body.is-native .insurance-card-content {
  gap: var(--oc-sp-1);
  padding-top: var(--oc-sp-1);
}

/* --- Titre, badge, prix --- */
body.is-native .insurance-header {
  gap: var(--oc-sp-2);
  align-items: baseline;
}

body.is-native .insurance-title-block {
  gap: var(--oc-sp-2);
}

body.is-native .insurance-title {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-lg);
  color: var(--oc-ink);
  letter-spacing: 0.3px;
  line-height: 1.15;
}

body.is-native .insurance-badge {
  padding: 3px var(--oc-sp-2);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-xs);
  font-weight: 700;
  letter-spacing: 0.3px;
  color: var(--oc-brand-700);
  background: var(--oc-brand-100);
  border-radius: var(--oc-r-sm);
}

body.is-native .insurance-option.insurance-selected .insurance-badge {
  background: var(--oc-surface);
}

body.is-native .insurance-price {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  color: var(--oc-brand);
}

/* --- Description et documents --- */
body.is-native .insurance-description {
  margin: var(--oc-sp-1) 0 var(--oc-sp-3);
  font-size: var(--oc-text-base);
  font-weight: 500;
  line-height: 1.45;
  color: var(--oc-ink-2);
}

body.is-native .insurance-links {
  gap: var(--oc-sp-2);
  margin-top: 0;
}

body.is-native .insurance-links a,
body.is-native .insurance-links a:hover {
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  padding: 0 var(--oc-sp-3);
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-ink-2);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-bottom-color: var(--oc-line-2);
  border-radius: var(--oc-r-full);
  opacity: 1;
}

/* --- Formulaire des assurés --- */
body.is-native .participants-form {
  margin-top: var(--oc-sp-4);
  padding: var(--oc-sp-5) var(--oc-sp-4);
  background: var(--oc-surface);
  border: 1px solid var(--oc-line);
  border-radius: var(--oc-r-lg);
  box-shadow: var(--oc-e1);
}

body.is-native .participants-title {
  margin: 0 0 var(--oc-sp-1);
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-title);
  font-weight: 400;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: var(--oc-ink);
}

body.is-native .participants-subtitle {
  display: block;
  margin: 0 0 var(--oc-sp-5);
  font-size: var(--oc-text-sm);
  color: var(--oc-ink-3);
}

body.is-native .participant-row {
  margin-bottom: var(--oc-sp-5);
  padding-bottom: var(--oc-sp-5);
  border-bottom: 1px solid var(--oc-line);
}

body.is-native .participant-header {
  margin-bottom: var(--oc-sp-3);
}

body.is-native .participant-number {
  font-family: var(--oc-font-display);
  font-size: var(--oc-text-base);
  font-weight: 400;
  letter-spacing: 0.4px;
  color: var(--oc-ink);
}

/* Une colonne : à 360px de large, trois champs côte à côte sont illisibles. */
body.is-native .participant-fields {
  grid-template-columns: 1fr;
  gap: var(--oc-sp-4);
}

body.is-native .field-group.oc-field {
  margin-bottom: 0;
}

body.is-native .field-group label {
  margin: 0;
  font-size: var(--oc-text-sm);
  font-weight: 700;
  text-transform: none;
  color: var(--oc-ink-2);
}

body.is-native .field-group input,
body.is-native .field-group select {
  min-height: var(--oc-tap);
  margin-top: 6px;
  padding: 0 var(--oc-sp-4);
  font-family: var(--oc-font-text);
  /* 16px minimum : en dessous, iOS zoome à la mise au point du champ. */
  font-size: var(--oc-text-base);
  font-weight: 600;
  color: var(--oc-ink);
  background-color: var(--oc-surface);
  border: 1px solid var(--oc-line-2);
  border-radius: var(--oc-r-md);
  transition: border-color var(--oc-fast) var(--oc-ease),
              box-shadow var(--oc-fast) var(--oc-ease);
}

body.is-native .field-group input:focus,
body.is-native .field-group select:focus {
  border-color: var(--oc-brand);
  box-shadow: 0 0 0 3px var(--oc-brand-100);
}

body.is-native .field-group .field-error {
  border-color: var(--oc-danger) !important;
}

body.is-native .error-message {
  margin-top: 6px;
  margin-left: 0;
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-danger);
}

/* --- Chargement --- */
body.is-native .insurance-loading-inline {
  display: none;
}

body.is-native .insurance-skeletons {
  display: flex;
  flex-direction: column;
  gap: var(--oc-sp-3);
}

body.is-native .insurance-skeleton {
  height: 132px;
  border-radius: var(--oc-r-lg);
}

body.is-native .reloading-indicator {
  margin-bottom: var(--oc-sp-3);
  padding: var(--oc-sp-2) var(--oc-sp-4);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-sm);
  font-weight: 600;
  color: var(--oc-ink-2);
  background: var(--oc-surface-2);
  border-radius: var(--oc-r-full);
}

body.is-native .small-spinner {
  border-color: var(--oc-line);
  border-top-color: var(--oc-brand);
}

body.is-native .loading-spinner {
  border-color: var(--oc-line);
  border-top-color: var(--oc-brand);
}

/* --- Indisponibilité --- */
body.is-native .insurance-error {
  padding: var(--oc-sp-5) var(--oc-sp-4);
  background: var(--oc-danger-bg);
  border: 0;
  border-radius: var(--oc-r-lg);
}

body.is-native .insurance-error p {
  margin-bottom: var(--oc-sp-2);
  font-family: var(--oc-font-text);
  font-size: var(--oc-text-base);
  line-height: 1.45;
  color: var(--oc-danger);
}

body.is-native .insurance-error .error-detail {
  font-size: var(--oc-text-sm);
  font-weight: 500;
  color: var(--oc-ink-2);
}

/* =========================================================================
   ASSURANCE — rendu de l'application mobile (préfixe rvi-)
   Ces règles n'habillent que le balisage natif, lui-même rendu sous
   `v-if="isNative"`. Le site conserve strictement son apparence.
   ========================================================================= */

body.is-native .rvi p,
body.is-native .rvi ul,
body.is-native .rvi li,
body.is-native .rvi span,
body.is-native .rvi div,
body.is-native .rvi label,
body.is-native .rvi a,
body.is-native .rvi input,
body.is-native .rvi select {
  all: unset;
  display: revert;
  box-sizing: border-box;
}

body.is-native .rvi {
  --rvi-bg: #F5F0E6;
  --rvi-surface: #EFE8DA;
  --rvi-ink: #28201C;
  --rvi-ink-2: #6B5E54;
  --rvi-ink-3: #8C7E73;
  --rvi-red: #DD4124;
  --rvi-line: rgba(107, 94, 84, 0.18);

  display: flex;
  flex-direction: column;
  gap: 10px;
  font-family: var(--oc-font-text);
  color: var(--rvi-ink);
}

body.is-native .rvi .rvi__label {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

body.is-native .rvi .rvi__brand {
  flex: 0 0 auto;
  padding: 2px 6px;
  border-radius: 5px;
  background: #0E3B5C;
  color: #FAF6EE;
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.5px;
}

/* ---------- Attente ---------- */

body.is-native .rvi .rvi__wait {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

body.is-native .rvi .rvi__skel {
  display: block;
  height: 92px;
  border-radius: 14px;
  background: linear-gradient(90deg, #E9E1D3 25%, #F2ECE0 37%, #E9E1D3 63%);
  background-size: 400% 100%;
  animation: rvi-shine 1400ms ease-in-out infinite;
}

body.is-native .rvi .rvi__reload {
  font-size: 12px;
  font-weight: 700;
  color: var(--rvi-ink-2);
}

body.is-native .rvi .rvi__ko {
  padding: 10px 12px;
  border-radius: 12px;
  background: #FDF3E2;
  border: 1px solid #E9CFA0;
}

body.is-native .rvi .rvi__ko p {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  color: #B87400;
}

body.is-native .rvi .rvi__ko-sub {
  margin-top: 4px;
  font-weight: 600 !important;
  font-size: 12px !important;
}

/* ---------- Formules ---------- */

body.is-native .rvi .rvi__list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

body.is-native .rvi .rvi__list > li {
  display: block;
  list-style: none;
}

body.is-native .rvi .rvi__card {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 12px;
  border-radius: 14px;
  background: var(--rvi-bg);
  border: 2px solid transparent;
  cursor: pointer;
  transition: border-color 180ms ease, background-color 180ms ease;
}

body.is-native .rvi .rvi__card.is-on {
  border-color: var(--rvi-red);
  background: #FBEDE9;
}

body.is-native .rvi .rvi__input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

body.is-native .rvi .rvi__input:focus-visible ~ .rvi__head .rvi__mark {
  outline: 2px solid var(--rvi-red);
  outline-offset: 2px;
}

body.is-native .rvi .rvi__reco {
  position: absolute;
  top: -9px;
  right: 12px;
  padding: 2px 7px;
  border-radius: 6px;
  background: var(--rvi-red);
  color: #FAF6EE;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

body.is-native .rvi .rvi__head {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Marque de sélection : la carte entière est la cible, mais un repère
   explicite évite d'avoir à deviner laquelle est retenue. */
body.is-native .rvi .rvi__mark {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid rgba(107, 94, 84, 0.3);
  background: var(--rvi-surface);
  color: transparent;
  transition: background-color 160ms ease, border-color 160ms ease, color 160ms ease;
}

body.is-native .rvi .rvi__card.is-on .rvi__mark {
  background: var(--rvi-red);
  border-color: var(--rvi-red);
  color: #FAF6EE;
}

body.is-native .rvi .rvi__name {
  flex: 1 1 auto;
  min-width: 0;
  font-family: var(--oc-font-display);
  font-size: 15px;
  line-height: 1.15;
  text-transform: uppercase;
}

body.is-native .rvi .rvi__price {
  flex: 0 0 auto;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

body.is-native .rvi .rvi__price-wait {
  flex: 0 0 auto;
  display: block;
  width: 54px;
  height: 16px;
  border-radius: 5px;
  background: #E5DDD0;
  animation: rvi-pulse 1200ms ease-in-out infinite;
}

body.is-native .rvi .rvi__desc {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--rvi-ink-2);
}

body.is-native .rvi .rvi__links {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  padding-top: 7px;
  border-top: 1px dashed var(--rvi-line);
}

body.is-native .rvi .rvi__links a {
  font-size: 11px;
  font-weight: 700;
  color: var(--rvi-ink-3);
  text-decoration: underline;
  cursor: pointer;
  /* Cible tactile sans écarter visuellement les liens. */
  padding: 8px 0;
  margin: -8px 0;
}

/* ---------- Assurés ---------- */

body.is-native .rvi .rvi__people {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 10px;
  border-top: 1px solid var(--rvi-line);
}

body.is-native .rvi .rvi__people-title {
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
}

body.is-native .rvi .rvi__people-sub {
  font-size: 12px;
  font-weight: 600;
  color: var(--rvi-ink-2);
  margin-top: -4px;
}

body.is-native .rvi .rvi__person {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  border-radius: 12px;
  background: var(--rvi-bg);
  border: 1px solid var(--rvi-line);
}

body.is-native .rvi .rvi__person-num {
  font-family: var(--oc-font-display);
  font-size: 14px;
  text-transform: uppercase;
}

body.is-native .rvi .rvi__fields {
  display: flex;
  gap: 6px;
}

/* 16px sur les champs : en dessous, iOS zoome au focus. */
body.is-native .rvi .rvi__sel,
body.is-native .rvi .rvi__in {
  height: 46px;
  padding: 0 10px;
  border-radius: 10px;
  background: var(--rvi-surface);
  border: 1.5px solid var(--rvi-line);
  font-family: var(--oc-font-text);
  font-size: 16px;
  font-weight: 600;
  color: var(--rvi-ink);
}

body.is-native .rvi .rvi__sel {
  flex: 0 0 74px;
  width: 74px;
  padding: 0 6px;
  font-weight: 700;
}

body.is-native .rvi .rvi__in {
  flex: 1 1 auto;
  min-width: 0;
}

body.is-native .rvi .rvi__in::placeholder { color: var(--rvi-ink-3); }

body.is-native .rvi .rvi__sel:focus,
body.is-native .rvi .rvi__in:focus {
  border-color: var(--rvi-red);
  outline: none;
}

body.is-native .rvi .rvi__sel.is-ko,
body.is-native .rvi .rvi__in.is-ko { border-color: #C2341A; }

@keyframes rvi-shine { to { background-position: -135% 0; } }
@keyframes rvi-pulse { 50% { opacity: 0.45; } }

@media (prefers-reduced-motion: reduce) {
  body.is-native .rvi .rvi__skel,
  body.is-native .rvi .rvi__price-wait { animation: none; }
}
</style>